/* =========================================================
   Flexi › Vernerunder — pure logic shared by the round page, the list and the
   template editor. No Angular, no HTTP: everything here is unit tested in
   safety-round.logic.spec.ts.
   ========================================================= */

import {
  DeviationImportance,
  SafetyRoundDueDays,
  SafetyRoundGrade,
  SafetyRoundItem,
  SafetyRoundTemplateItem,
} from './safety-round.models';

/** Which checkpoints the round page lists. */
export type SafetyRoundItemFilter = 'all' | 'unanswered' | 'findings';

/** Grades 2–4 are findings and must be followed up as deviations. */
export function isFinding(grade: SafetyRoundGrade | null | undefined): boolean {
  return grade === SafetyRoundGrade.ShouldImprove
    || grade === SafetyRoundGrade.LegalBreach
    || grade === SafetyRoundGrade.DangerToLifeOrHealth;
}

export function isGraded(item: Pick<SafetyRoundItem, 'Grade'>): boolean {
  return item.Grade !== null && item.Grade !== undefined;
}

/**
 * Deviation importance a finding is registered with — the backend applies the
 * same mapping (2 → Low, 3 → Medium, 4 → High). Null for anything else.
 */
export function importanceForGrade(grade: SafetyRoundGrade | null | undefined): DeviationImportance | null {
  switch (grade) {
    case SafetyRoundGrade.ShouldImprove:
      return DeviationImportance.Low;
    case SafetyRoundGrade.LegalBreach:
      return DeviationImportance.Medium;
    case SafetyRoundGrade.DangerToLifeOrHealth:
      return DeviationImportance.High;
    default:
      return null;
  }
}

/** Days until the suggested deadline for a finding grade; null for a non-finding. */
export function dueDaysForGrade(grade: SafetyRoundGrade | null | undefined, dueDays: SafetyRoundDueDays): number | null {
  switch (grade) {
    case SafetyRoundGrade.ShouldImprove:
      return dueDays.ShouldImprove;
    case SafetyRoundGrade.LegalBreach:
      return dueDays.LegalBreach;
    case SafetyRoundGrade.DangerToLifeOrHealth:
      return dueDays.Danger;
    default:
      return null;
  }
}

/**
 * Suggested deadline for a finding: `from` plus the grade's due days, as a
 * local calendar day (time stripped). Null for a non-finding grade.
 */
export function suggestedDeadline(
  grade: SafetyRoundGrade | null | undefined,
  dueDays: SafetyRoundDueDays,
  from: Date,
): Date | null {
  const days = dueDaysForGrade(grade, dueDays);
  if (days === null) {
    return null;
  }
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
}

/**
 * Whether the template asks for photo evidence at the item's current grade:
 * RequirePhotoAtGrade3 for grade 3, RequirePhotoAtGrade4 for grade 4.
 */
export function photoRequired(item: Pick<SafetyRoundItem, 'Grade' | 'RequirePhotoAtGrade3' | 'RequirePhotoAtGrade4'>): boolean {
  return (item.Grade === SafetyRoundGrade.LegalBreach && item.RequirePhotoAtGrade3)
    || (item.Grade === SafetyRoundGrade.DangerToLifeOrHealth && item.RequirePhotoAtGrade4);
}

/** The backend refuses the deviation (400) while a required photo is missing. */
export function missingRequiredPhoto(
  item: Pick<SafetyRoundItem, 'Grade' | 'RequirePhotoAtGrade3' | 'RequirePhotoAtGrade4' | 'Photos'>,
): boolean {
  return photoRequired(item) && !(item.Photos?.length);
}

/** Upload limits, mirroring the backend's 400/409. */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const PHOTO_MAX_COUNT = 10;
export const PHOTO_ACCEPT = 'image/png,image/jpeg,image/webp,image/heic';
const PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif'];
const PHOTO_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'];

/**
 * Why a picked file cannot be uploaded, or null when it can. Some browsers
 * report HEIC with an empty type, so the extension is accepted as well.
 */
export function photoFileError(file: Pick<File, 'type' | 'size' | 'name'>): 'type' | 'size' | null {
  const extension = (file.name.split('.').pop() ?? '').toLowerCase();
  const typeOk = PHOTO_TYPES.includes(file.type.toLowerCase())
    || (!file.type && PHOTO_EXTENSIONS.includes(extension));
  if (!typeOk) {
    return 'type';
  }
  return file.size > PHOTO_MAX_BYTES ? 'size' : null;
}

/**
 * A local calendar day as "yyyy-MM-dd". Deliberately not `toISOString()`, which
 * converts to UTC first and moves a Norwegian midnight to the previous day.
 */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * A calendar date from the API ("yyyy-MM-dd", or the date part of an ISO
 * string) as DD-MM-YYYY, the display format of every safety-round screen.
 * Read from the string rather than through `Date`, so no timezone can move the
 * day. Anything unrecognised is returned unchanged.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
}

/** Fallback download name for a round's report: "Vernerunde DD-MM-YYYY.pdf". */
export function reportFileName(prefix: string, roundDate: string): string {
  return `${prefix} ${formatCalendarDate(roundDate)}.pdf`;
}

/** Counts per grade. `ungraded` counts items with no grade yet. */
export interface GradeDistribution {
  notApplicable: number;
  satisfactory: number;
  shouldImprove: number;
  legalBreach: number;
  danger: number;
  ungraded: number;
}

export function gradeDistribution(items: readonly Pick<SafetyRoundItem, 'Grade'>[]): GradeDistribution {
  const result: GradeDistribution = {
    notApplicable: 0,
    satisfactory: 0,
    shouldImprove: 0,
    legalBreach: 0,
    danger: 0,
    ungraded: 0,
  };
  for (const item of items) {
    switch (item.Grade) {
      case SafetyRoundGrade.NotApplicable:
        result.notApplicable++;
        break;
      case SafetyRoundGrade.Satisfactory:
        result.satisfactory++;
        break;
      case SafetyRoundGrade.ShouldImprove:
        result.shouldImprove++;
        break;
      case SafetyRoundGrade.LegalBreach:
        result.legalBreach++;
        break;
      case SafetyRoundGrade.DangerToLifeOrHealth:
        result.danger++;
        break;
      default:
        result.ungraded++;
    }
  }
  return result;
}

/** Graded / total, plus the findings still missing a deviation. */
export interface SafetyRoundProgress {
  graded: number;
  total: number;
  /** 0–100, rounded down so "almost done" never reads as 100 %. */
  percent: number;
  findings: number;
  findingsWithoutDeviation: number;
}

export function roundProgress(items: readonly SafetyRoundItem[]): SafetyRoundProgress {
  const total = items.length;
  const graded = items.filter(isGraded).length;
  const findings = items.filter(item => isFinding(item.Grade));
  return {
    graded,
    total,
    percent: total === 0 ? 0 : Math.floor((graded * 100) / total),
    findings: findings.length,
    findingsWithoutDeviation: findings.filter(item => !item.Deviation).length,
  };
}

/** Percentage for a summary row's progress bar. */
export function percentOf(part: number, total: number): number {
  return total <= 0 ? 0 : Math.min(100, Math.floor((part * 100) / total));
}

/**
 * What still stands between the round and completion. Mirrors the backend's
 * 400: every item graded, every finding linked to a deviation, and the
 * confirmation ticked.
 */
export interface CompletionCheck {
  ungraded: number;
  findingsWithoutDeviation: number;
  canComplete: boolean;
}

export function completionCheck(items: readonly SafetyRoundItem[], confirmed: boolean): CompletionCheck {
  const progress = roundProgress(items);
  const ungraded = progress.total - progress.graded;
  return {
    ungraded,
    findingsWithoutDeviation: progress.findingsWithoutDeviation,
    canComplete: items.length > 0 && ungraded === 0 && progress.findingsWithoutDeviation === 0 && confirmed,
  };
}

/** One area of a round, in display order. */
export interface SafetyRoundSection {
  name: string;
  position: number;
  items: SafetyRoundItem[];
  graded: number;
  findings: number;
}

/**
 * Groups items into their areas, ordered by SectionPosition and then Position.
 * Ad-hoc items carry their section's name, so they join the same area.
 */
export function groupSections(items: readonly SafetyRoundItem[]): SafetyRoundSection[] {
  const sorted = [...items].sort((a, b) =>
    a.SectionPosition - b.SectionPosition || a.Position - b.Position || a.Id - b.Id);
  const sections: SafetyRoundSection[] = [];
  for (const item of sorted) {
    let section = sections.find(s => s.name === item.SectionName);
    if (!section) {
      section = { name: item.SectionName, position: item.SectionPosition, items: [], graded: 0, findings: 0 };
      sections.push(section);
    }
    section.items.push(item);
    if (isGraded(item)) {
      section.graded++;
    }
    if (isFinding(item.Grade)) {
      section.findings++;
    }
  }
  return sections;
}

/** Whether an item passes the round page's filter chip and search box. */
export function matchesItemFilter(item: SafetyRoundItem, filter: SafetyRoundItemFilter, query: string): boolean {
  if (filter === 'unanswered' && isGraded(item)) {
    return false;
  }
  if (filter === 'findings' && !isFinding(item.Grade)) {
    return false;
  }
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [item.Title, item.Guidance ?? '', item.SectionName, item.Comment ?? '']
    .some(text => text.toLowerCase().includes(needle));
}

/** Findings for the completion step: most severe first, then in round order. */
export function sortedFindings(items: readonly SafetyRoundItem[]): SafetyRoundItem[] {
  return items
    .filter(item => isFinding(item.Grade))
    .sort((a, b) =>
      (b.Grade ?? 0) - (a.Grade ?? 0)
      || a.SectionPosition - b.SectionPosition
      || a.Position - b.Position);
}

// ---------------------------------------------------------------------------
// Template editor
// ---------------------------------------------------------------------------

/** An area of a template while it is being edited. */
export interface TemplateSectionDraft {
  name: string;
  items: SafetyRoundTemplateItem[];
}

/** Template items grouped into editable areas, in their stored order. */
export function toTemplateSections(items: readonly SafetyRoundTemplateItem[]): TemplateSectionDraft[] {
  const sorted = [...items].sort((a, b) => a.SectionPosition - b.SectionPosition || a.Position - b.Position);
  const sections: TemplateSectionDraft[] = [];
  for (const item of sorted) {
    const last = sections[sections.length - 1];
    if (last && last.name === item.SectionName) {
      last.items.push({ ...item });
    } else {
      sections.push({ name: item.SectionName, items: [{ ...item }] });
    }
  }
  return sections;
}

/**
 * Flattens edited areas back into the save request's item list, renumbering
 * SectionPosition and Position from the on-screen order (both 1-based). Empty
 * areas are dropped: an area exists only through its checkpoints.
 */
export function flattenTemplateSections(sections: readonly TemplateSectionDraft[]): SafetyRoundTemplateItem[] {
  const result: SafetyRoundTemplateItem[] = [];
  let sectionPosition = 0;
  for (const section of sections) {
    if (!section.items.length) {
      continue;
    }
    sectionPosition++;
    section.items.forEach((item, index) => {
      result.push({
        ...item,
        SectionName: section.name.trim(),
        SectionPosition: sectionPosition,
        Position: index + 1,
        Title: item.Title.trim(),
        Guidance: item.Guidance?.trim() ? item.Guidance.trim() : null,
        StandardTexts: item.StandardTexts.map(text => text.trim()).filter(text => text.length > 0),
        RegulatoryReferences: item.RegulatoryReferences
          .map(ref => ({ Title: ref.Title.trim(), Url: ref.Url?.trim() ? ref.Url.trim() : null }))
          .filter(ref => ref.Title.length > 0),
      });
    });
  }
  return result;
}

/**
 * Moves one element by `delta` places, returning a new array. A move past
 * either end returns the list unchanged.
 */
export function moveInList<T>(list: readonly T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) {
    return [...list];
  }
  const copy = [...list];
  const [moved] = copy.splice(index, 1);
  copy.splice(target, 0, moved);
  return copy;
}

/** Standard texts typed one per line; blank lines are dropped. */
export function parseStandardTexts(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0);
}

/**
 * Appends a standard text to a comment on its own line, without doubling a
 * text that is already there.
 */
export function appendStandardText(comment: string | null, text: string): string {
  const current = (comment ?? '').trimEnd();
  if (!current) {
    return text;
  }
  if (current.split(/\r?\n/).some(line => line.trim() === text.trim())) {
    return current;
  }
  return `${current}\n${text}`;
}
